import { Dispatch, SetStateAction, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import useModalViewKrpano from 'pages/building/panoramas/hooks/useModalViewKrpano'
import { useRecoilState, useRecoilValue } from 'recoil'
import { selectedPointDetail, selectedSubImageState } from 'store/buildingStore'
import useSWR from 'swr'
import { Point } from 'types/building'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'

import Button from 'components/atoms/Button'
import Modal from 'components/atoms/Modal'

export default function ModalSetInitViewSubImage({
  openModalSetInitView,
  setOpenModalSetInitView
}: {
  openModalSetInitView: boolean
  setOpenModalSetInitView: Dispatch<SetStateAction<boolean>>
}) {
  const { t } = useTranslation()
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const { embedpanoModal } = useModalViewKrpano()
  const selectedSubImage = useRecoilValue(selectedSubImageState)
  const { trigger, isMutating } = useMutation(`v1/points/${pointDetail?.id}`)
  const { id, floorId } = useParams()
  const { navigate } = useDirectory()
  const hasEmbeddedRef = useRef(false)

  const { data: detailPoint, mutate: refreshDetailPoint } = useSWR<Point>(
    openModalSetInitView && pointDetail?.id
      ? {
          url: `v1/points/${pointDetail?.id}?building_id=${id}&floor_id=${floorId}`
        }
      : null,
    {
      revalidateOnMount: false
    }
  )

  const handleCancel = () => {
    setOpenModalSetInitView(false)
  }
  useEffect(() => {
    if (!openModalSetInitView || !pointDetail) return

    if (hasEmbeddedRef.current) return
    hasEmbeddedRef.current = true

    const { image360, subImages, initView: initViewMain } = pointDetail

    if (image360) {
      const oldMainPano = new KrpanoEngine('preview-krpano-container-main-pano')
        .instance
      oldMainPano?.remove?.()
      embedpanoModal(
        {
          imageUrl: image360.s3Path,
          previewUrl: image360.thumbnail,
          h: initViewMain?.h ?? 0,
          v: initViewMain?.v ?? 0,
          z: initViewMain?.z ?? 100,
          tilesUrl: image360.tilesUrl || '',
          multires: image360.multires || ''
        },
        'preview-krpano-container-main'
      )
    }
    const sub = subImages?.find((sub) => sub.id === selectedSubImage)
    if (subImages) {
      const oldSubPano = new KrpanoEngine('preview-krpano-container-sub-pano')
        .instance
      oldSubPano?.remove?.()
      embedpanoModal(
        {
          imageUrl: sub?.s3Path || '',
          previewUrl: sub?.thumbnail,
          h: sub?.initView?.h ?? 0,
          v: sub?.initView?.v ?? 0,
          z: sub?.initView?.z ?? 100,
          tilesUrl: sub?.tilesUrl || '',
          multires: sub?.multires || ''
        },
        'preview-krpano-container-sub'
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openModalSetInitView, pointDetail, embedpanoModal])

  const handleSave = () => {
    const krpano = new KrpanoEngine('preview-krpano-container-sub-pano')
    const newH = krpano.get('view.hlookat')
    const newV = krpano.get('view.vlookat')
    const newZ = krpano.get('view.fov')

    if (!isMutating) {
      trigger(
        {
          method: 'patch',
          data: {
            floor_id: +(floorId || 0),
            building_id: +(id || 0),
            init_view: {
              h: newH,
              v: newV,
              z: newZ,
              radar_h_gap: pointDetail?.initView.radarHGap
            },
            ...(selectedSubImage ? { sub_image_id: selectedSubImage } : {}),
            is_sub_image: true
          },
          successMessage: t('building.finishedSetting')
        },
        {
          onSuccess: async () => {
            if (selectedSubImage) {
              setPointDetail((prev) => {
                if (!prev) return prev

                const updatedSubImages = prev.subImages?.map((item) => {
                  if (item.id === Number(selectedSubImage)) {
                    return {
                      ...item,
                      initView: {
                        h: newH,
                        v: newV,
                        z: newZ,
                        radarHGap: item.initView?.radarHGap ?? 0
                      }
                    }
                  }
                  return item
                })

                return {
                  ...prev,
                  subImages: updatedSubImages
                }
              })
            } else {
              setPointDetail(
                (prev) =>
                  prev && {
                    ...prev,
                    initView: {
                      ...prev.initView,
                      h: newH,
                      v: newV,
                      z: newZ
                    }
                  }
              )
            }
            await refreshDetailPoint()
            setOpenModalSetInitView(false)
          },
          onError: (error) => {
            Utils.handleErrorNavigate(error, navigate)
          }
        }
      )
    } else {
      notification.success({
        message: t('building.finishedSetting')
      })
    }
  }

  useEffect(() => {
    const subImagePending = detailPoint?.subImages?.find(
      (item) => item.id === selectedSubImage
    )
    const krpano = new KrpanoEngine('full').instance

    krpano.call(
      `load_view_direction(${subImagePending?.initView?.h}, ${subImagePending?.initView?.v}, ${subImagePending?.initView?.z})`
    )

    const delta = Krpano.GetSubImageDelta(detailPoint, selectedSubImage)
    Krpano.RevalidateDirection(detailPoint?.directions || [], delta)
    Krpano.RevalidateStairway(detailPoint?.stairways || [], delta)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailPoint])

  const handleReset = () => {
    const krpano = new KrpanoEngine('preview-krpano-container-sub-pano')

    krpano.call('set_angle_for_scene()')
  }

  return (
    <Modal width={1074} className="relative">
      <div className="absolute w-[2px] h-[85%] bg-[#DB0000] z-10 left-[50%] top-[55%] translate-x-[-50%] translate-y-[-51%]" />
      <div className="text-2xl font-bold text-center mb-[31px]">
        {t('building.setInitViewTitleModal')}
      </div>
      <div>
        <div className="grid grid-cols-5 gap-x-8 items-start">
          <div className="col-span-1 flex justify-end">
            <div className="text-2xl font-bold w-20 text-center">
              {t('building.mainImageTitle')}
            </div>
          </div>
          <div className="col-span-3 bg-red-400 text-center h-[320px]">
            <div className="w-full h-full">
              <div id="preview-krpano-container-main" className="h-full" />
            </div>
          </div>
          <div />
        </div>
        <br />
        <div className="grid grid-cols-5 gap-x-8 items-start">
          <div className="col-span-1 flex justify-end">
            <div className="text-2xl font-bold w-20 text-center">
              {t('building.subImageTitle')}
            </div>
          </div>
          <div className="col-span-3 bg-red-400 text-center h-[320px]">
            <div className="w-full h-full">
              <div id="preview-krpano-container-sub" className="h-full" />
            </div>
          </div>
          <div className="col-span-1 w-full h-full flex justify-end items-end">
            <div className="">
              <Button sizing="w-[140px] h-[50px]" onClick={handleCancel}>
                {t('building.buttonCancel')}
              </Button>
              <Button sizing="w-[140px] h-[50px] my-6" onClick={handleReset}>
                {t('building.buttonResetToDefault')}
              </Button>
              <Button
                type="primary"
                sizing="w-[140px] h-[50px]"
                onClick={handleSave}
              >
                {t('building.buttonSave')}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  )
}
